// Game-level record + replay. Positions are always derived by replaying the
// move list — never stored. Phases: active -> scoring (two passes or move cap)
// -> finished; 'resign' finishes immediately.

import type { BoardSize, Color, GameStatus, GameMode, Vertex } from '../types.ts';
import { emptyPosition, type Position } from './board.ts';
import { applyMove, applyPass, checkMove, type IllegalReason } from './rules.ts';
import { isBoardVertex, isPass, isResign, ptToVertex, vertexToPt } from './coords.ts';

export interface GameMove {
  color: Color;
  vertex: Vertex;                             // 'Q16' | 'pass' | 'resign'
}

export interface GameRecord {
  boardSize: BoardSize;
  komi: number;
  mode: GameMode;
  humanColor: Color | null;                   // null for hotseat
  moves: GameMove[];
  /**
   * Move counts at which a scoring dispute was resumed: replay resets
   * consecutivePasses to 0 (and status to active) after processing that many
   * moves. The passes themselves stay in the record.
   */
  resumePoints?: number[];
}

export function opposite(color: Color): Color {
  return color === 'B' ? 'W' : 'B';
}

/** Hard move cap; reaching it forces the scoring phase (long-cycle safety net). */
export function moveCap(size: number): number {
  return 2 * size * size;
}

export function resignResult(winner: Color): string {
  return `${winner}+R`;
}

export interface ReplayState {
  position: Position;
  toPlay: Color;
  status: GameStatus;
  consecutivePasses: number;
  captures: { B: number; W: number };         // stones captured BY each color
  capturedByMove: Vertex[][];                 // per replayed move, vertices it captured
  moveCount: number;
  winner: Color | null;                       // set only on resign
  result: string | null;                      // set only on resign ('B+R' / 'W+R')
}

export interface ReplayOptions {
  /** Override the move cap (tests only; defaults to moveCap(boardSize)). */
  moveCap?: number;
}

/**
 * Replay the first `upto` moves (default: all). Throws on malformed records:
 * wrong color to move, illegal board move, or moves after scoring/finished
 * without a matching resume point.
 */
export function replay(record: GameRecord, upto?: number, opts: ReplayOptions = {}): ReplayState {
  const size = record.boardSize;
  const cap = opts.moveCap ?? moveCap(size);
  const resumePoints = new Set(record.resumePoints ?? []);
  const count = Math.min(upto ?? record.moves.length, record.moves.length);

  let position = emptyPosition(size);
  let toPlay: Color = 'B';
  let status: GameStatus = 'active';
  let consecutivePasses = 0;
  const captures = { B: 0, W: 0 };
  const capturedByMove: Vertex[][] = [];
  let winner: Color | null = null;
  let result: string | null = null;

  for (let i = 0; i < count; i++) {
    const move = record.moves[i]!;
    if (status === 'finished') {
      throw new Error(`replay: move ${i + 1} after game finished`);
    }
    if (status === 'scoring') {
      throw new Error(`replay: move ${i + 1} during scoring without a resume point`);
    }
    if (move.color !== toPlay) {
      throw new Error(`replay: move ${i + 1} expected ${toPlay}, got ${move.color}`);
    }

    if (isResign(move.vertex)) {
      status = 'finished';
      winner = opposite(move.color);
      result = resignResult(winner);
      capturedByMove.push([]);
    } else if (isPass(move.vertex)) {
      position = applyPass(position);
      consecutivePasses += 1;
      capturedByMove.push([]);
      if (consecutivePasses >= 2) status = 'scoring';
    } else {
      const pt = vertexToPt(move.vertex, size);
      const res = applyMove(position, move.color, pt);
      position = res.position;
      captures[move.color] += res.captured.length;
      capturedByMove.push(res.captured.map((p) => ptToVertex(p, size)));
      consecutivePasses = 0;
    }

    toPlay = opposite(move.color);
    if (status !== 'finished' && resumePoints.has(i + 1)) {
      status = 'active';
      consecutivePasses = 0;
    }
  }

  if (status === 'active' && count >= cap) status = 'scoring';

  return {
    position,
    toPlay,
    status,
    consecutivePasses,
    captures,
    capturedByMove,
    moveCount: count,
    winner,
    result,
  };
}

/**
 * How many plies an undo removes, or 0 if undo is impossible.
 * - a record finished by resignation: 0 (undo must never un-resign a game).
 * - hotseat: 1 ply.
 * - vs bot: pop from the end until it is the human's turn again AND at least
 *   one human move was removed (normally 2 plies; 1 while the bot is to move;
 *   0 if the human has not moved yet, e.g. bot opened as Black).
 */
export function computeUndoPlies(record: GameRecord): number {
  const moves = record.moves;
  if (moves.length === 0) return 0;
  if (isResign(moves[moves.length - 1]!.vertex)) return 0;
  if (record.mode === 'hotseat') return 1;
  const humanColor = record.humanColor;
  if (humanColor === null) return 0;

  let removed = 0;
  let humanRemoved = 0;
  for (let i = moves.length - 1; i >= 0; i--) {
    removed += 1;
    if (moves[i]!.color === humanColor) humanRemoved += 1;
    const toPlayAfter: Color = i > 0 ? opposite(moves[i - 1]!.color) : 'B';
    if (humanRemoved >= 1 && toPlayAfter === humanColor) return removed;
  }
  return 0;
}

/**
 * Legality of a vertex for `color` on a position (client hover / server
 * validation). 'pass' and 'resign' are always legal; malformed vertices
 * report 'out_of_bounds'.
 */
export function vertexLegality(
  position: Position,
  color: Color,
  vertex: Vertex,
): IllegalReason | null {
  if (!isBoardVertex(vertex)) return null;
  let pt: number;
  try {
    pt = vertexToPt(vertex, position.size);
  } catch {
    return 'out_of_bounds';
  }
  return checkMove(position, color, pt);
}
